import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import ApiError from 'api/ApiError';
import type {
    HygienistProps,
    JobProps,
    MembershipOverlayState,
    MembershipRestrictionErrorData,
    MembershipStatusCategory,
    MembershipStatusResponse,
    TempShiftAccess,
} from './types';

dayjs.extend(isSameOrBefore);

export type MembershipBannerCopy = {
    tone: 'info' | 'warning' | 'error';
    title: string;
    body: string;
    inlineAction?: {
        label: string;
        path: 'hygienistContactSupport';
    };
};

type MembershipBannerContext = {
    firstName?: string | null;
};

export type AvailableJobsMembershipCta = {
    label: string;
    path: 'coursesHome' | 'myStats';
};

const isRecord = (value: unknown): value is Record<string, any> => {
    return !!value && typeof value === 'object' && !Array.isArray(value);
};

export type MembershipDecisionInput = {
    mode?: MembershipStatusResponse['mode'] | null;
    currentStatusCode?: string | null;
    tempShiftAccess?: TempShiftAccess | null;
    overlay?: Pick<NonNullable<MembershipStatusResponse['overlay']>, 'active' | 'enforced' | 'state'> | null;
    membershipAuthorityAvailable?: true;
    membershipOverlayState?: MembershipOverlayState | null;
};

export type JobRequesterMembershipDecision = MembershipDecisionInput & {
    membershipAuthorityAvailable: true;
    currentStatusCode: string;
    displayLabel: string | null;
    statusCategory: MembershipStatusCategory | null;
    tempShiftAccess: TempShiftAccess;
};

const TEMP_SHIFT_ACCESS_VALUES = new Set<TempShiftAccess>([
    'full',
    'restricted',
    'protected',
    'course_guidance',
]);

const MEMBERSHIP_STATUS_CODES = new Set([
    'Elite',
    'Pro',
    'Active',
    'Provisional',
    'RL1',
    'FL1',
    'RL1FL1',
    'RL2',
    'FL2',
    'RL2FL1',
    'RL1FL2',
    'RL2FL2',
    'CourseRequired',
]);

const MEMBERSHIP_OVERLAY_STATES = new Set<MembershipOverlayState>([
    'course_required',
    'restricted_reentry',
    'on_a_break',
    'not_a_fit',
    'duplicate_review',
]);

/**
 * Project the server-owned requester membership fields on a job-detail response
 * into the smallest shape needed for presentation decisions. The projection is
 * accepted only when the server explicitly says canonical authority is
 * available; compatibility fields such as `isReliable` are intentionally not
 * part of the contract.
 */
export const getJobRequesterMembershipDecision = (
    job?: Pick<JobProps,
        | 'membershipAuthorityAvailable'
        | 'membershipStatusCode'
        | 'membershipDisplayLabel'
        | 'membershipStatusCategory'
        | 'membershipTempShiftAccess'
        | 'membershipOverlayState'> | null,
): JobRequesterMembershipDecision | null => {
    const statusCode = typeof job?.membershipStatusCode === 'string'
        ? job.membershipStatusCode.trim()
        : '';
    if (
        job?.membershipAuthorityAvailable !== true
        || !statusCode
        || !MEMBERSHIP_STATUS_CODES.has(statusCode)
    ) return null;

    const tempShiftAccess = job.membershipTempShiftAccess
        && TEMP_SHIFT_ACCESS_VALUES.has(job.membershipTempShiftAccess)
        ? job.membershipTempShiftAccess
        : null;
    if (!tempShiftAccess) return null;
    if (
        job.membershipOverlayState != null
        && !MEMBERSHIP_OVERLAY_STATES.has(job.membershipOverlayState)
    ) return null;
    const membershipOverlayState = job.membershipOverlayState ?? null;

    return {
        membershipAuthorityAvailable: true,
        currentStatusCode: statusCode,
        displayLabel: typeof job.membershipDisplayLabel === 'string'
            ? job.membershipDisplayLabel
            : null,
        statusCategory: job.membershipStatusCategory ?? null,
        tempShiftAccess,
        membershipOverlayState,
    };
};

export const getApiErrorBody = (error: unknown): Record<string, any> | null => {
    if (error instanceof ApiError && isRecord(error.body)) {
        return error.body;
    }

    if (isRecord(error) && isRecord((error as any).body)) {
        return (error as any).body;
    }

    const rawMessage = isRecord(error) ? (error as any).message : null;
    if (typeof rawMessage !== 'string') return null;

    try {
        const parsed = JSON.parse(rawMessage);
        return isRecord(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export const getMembershipRestrictionError = (error: unknown): MembershipRestrictionErrorData | null => {
    const body = getApiErrorBody(error);
    const data = body?.data;
    if (isRecord(data) && data.type === 'membership_restriction') {
        return data as MembershipRestrictionErrorData;
    }
    return null;
};

export const shouldShowMembershipBanner = (membership?: MembershipStatusResponse | null): boolean => {
    if (!membership) return false;
    if (membership.overlay?.active) return true;
    return membership.tempShiftAccess !== 'full';
};

/**
 * Available Jobs keeps a safe My Stats fallback, but Course Required members
 * should follow the API's concrete recovery action directly to Courses. The
 * path is allowlisted and requires both a Course Required state and the
 * authoritative API `coursesHome` action so unrelated/unknown actions cannot
 * redirect the feed CTA.
 */
export const getAvailableJobsMembershipCta = (
    membership?: MembershipStatusResponse | null,
): AvailableJobsMembershipCta => {
    const isCourseRequired = membership?.currentStatusCode === 'CourseRequired'
        || membership?.statusCategory === 'ffs_recovery_course_required'
        || membership?.statusCategory === 'provisional_course_required'
        || (!!membership?.overlay?.active && membership.overlay.state === 'course_required');

    if (isCourseRequired && membership?.nextAction?.ctaPath === 'coursesHome') {
        return {
            label: membership.nextAction.ctaLabel?.trim() || 'Go to Courses',
            path: 'coursesHome',
        };
    }

    return { label: 'View My Stats', path: 'myStats' };
};

/**
 * My Stats-specific banner predicate (T2-6060). When the compact retention
 * guidance section is visible it owns all protected-rollout messaging, so the
 * generic "You are protected during rollout" banner would be a duplicate.
 * Terminal overlays and genuine access restrictions keep their banners.
 */
export const shouldShowMembershipBannerOnMyStats = (membership?: MembershipStatusResponse | null): boolean => {
    if (!shouldShowMembershipBanner(membership)) return false;
    if (
        membership?.retentionInsights?.visible
        && !membership.overlay?.active
        && membership.tempShiftAccess === 'protected'
    ) {
        return false;
    }
    return true;
};

const isRecoveryOverlayEnforced = (membership?: MembershipDecisionInput | null): boolean => {
    if (membership?.membershipAuthorityAvailable && membership.membershipOverlayState) {
        return true;
    }
    const overlay = membership?.overlay;
    if (!membership || !overlay?.active) return false;

    return typeof overlay.enforced === 'boolean'
        ? overlay.enforced
        : (membership as MembershipStatusResponse).mode === 'enforced';
};

const isBlockingRecoveryOverlay = (membership?: MembershipDecisionInput | null): boolean => {
    if (!isRecoveryOverlayEnforced(membership)) return false;

    switch (membership?.membershipOverlayState ?? membership?.overlay?.state) {
        case 'course_required':
        case 'restricted_reentry':
        case 'on_a_break':
        case 'not_a_fit':
        case 'duplicate_review':
            return true;
        default:
            return false;
    }
};

/**
 * Soft FFS-vs-legacy gate (mirrors API `hasFfsAccessDespiteLegacyStatus`).
 * Soft overlays (Course Required / On a Break) do not fail this gate — booking
 * callers still enforce via validateLimitations. Do not use for Delete Account
 * copy or other UI that has no later restriction gate.
 */
export const hasFfsAccessDespiteLegacyStatus = (
    membership?: MembershipStatusResponse | null,
): boolean => {
    if (!membership?.currentStatusCode) return false;
    const overlay = membership.overlay;
    if (overlay?.active && (overlay.state === 'not_a_fit' || overlay.state === 'duplicate_review')) {
        return false;
    }
    return true;
};

const DELETE_ACCOUNT_GOOD_STANDING_CODES = new Set([
    'Active',
    'Pro',
    'Elite',
    'Provisional',
    'RL1',
    'FL1',
    'RL1FL1',
]);

const hasTerminalDeleteAccountHold = (
    membership?: MembershipDecisionInput | null,
): boolean => {
    const overlayState = membership?.membershipOverlayState ?? (
        membership?.overlay?.active ? membership.overlay.state : null
    );
    return overlayState === 'not_a_fit' || overlayState === 'duplicate_review';
};

/**
 * T2-6649 Delete Account / UI copy: whether FFS should lift a stale legacy
 * On Hold presentation. Stricter than `hasFfsAccessDespiteLegacyStatus` —
 * CourseRequired / RL-FL limited / unknown codes stay On Hold.
 */
export const isFfsGoodStandingForDeleteAccount = (
    membership?: MembershipDecisionInput | null,
): boolean => {
    if (!membership?.currentStatusCode) return false;
    if (hasTerminalDeleteAccountHold(membership)) return false;
    if (!DELETE_ACCOUNT_GOOD_STANDING_CODES.has(membership.currentStatusCode)) return false;
    if (membership.tempShiftAccess !== 'full' && membership.tempShiftAccess !== 'protected') {
        return false;
    }
    return true;
};

export type DeleteAccountMembershipCopy = 'new' | 'active' | 'on_hold' | 'none';

/**
 * Resolve which Delete Account state box to show from canonical membership.
 * When canonical membership is unavailable, no membership-specific box is
 * shown: raw Hygienist.status is compatibility data, not presentation authority.
 */
export const resolveDeleteAccountMembershipCopy = (
    hygienist: { completedJobs: number },
    membership?: MembershipStatusResponse | null,
): DeleteAccountMembershipCopy => {
    if (!membership?.currentStatusCode) return 'none';
    if (hasTerminalDeleteAccountHold(membership) || !isFfsGoodStandingForDeleteAccount(membership)) {
        return 'on_hold';
    }
    if (hygienist.completedJobs > 0) return 'active';
    return 'new';
};

export const shouldShowRestrictedJobsEmptyState = (
    membership?: MembershipStatusResponse | null,
): boolean => {
    if (!membership) return false;
    if (isBlockingRecoveryOverlay(membership)) return true;
    return membership.mode === 'enforced' && membership.tempShiftAccess === 'restricted';
};

export const shouldShowShiftVisibilityRestrictionBanner = (
    membership?: MembershipStatusResponse | null,
): boolean => {
    if (!membership) return false;

    // Recovery overlays are governed by their own API live flags, separate from
    // the top-level FFS membership mode returned here.
    if (isBlockingRecoveryOverlay(membership)) return true;

    if (membership.mode !== 'enforced') return false;

    return (membership.tempShiftAccess === 'restricted' && !!membership.restrictions)
        || membership.currentStatusCode === 'CourseRequired';
};

const formatBreakUntil = (value?: string | null): string | null => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
};

const formatCooldownUntil = (value?: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) {
        return formatBreakUntil(new Date(value).toISOString());
    }
    if (typeof value === 'string') return formatBreakUntil(value);
    return null;
};

export const parseBlockedUntilMs = (value: unknown): number | null => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const ms = new Date(String(value)).getTime();
    return Number.isFinite(ms) ? ms : null;
};

export const isDateBlockedByCancellationCooldown = (
    dateKey: string,
    blockedUntil: unknown,
): boolean => {
    const blockedUntilMs = parseBlockedUntilMs(blockedUntil);
    if (blockedUntilMs == null) return false;
    return dayjs(dateKey).isSameOrBefore(dayjs(blockedUntilMs), 'day');
};

export const getCancellationCooldownDialogCopy = (
    blockedUntil: unknown,
): MembershipRestrictionDialogCopy => {
    const blockedUntilIso = typeof blockedUntil === 'string'
        ? blockedUntil
        : (parseBlockedUntilMs(blockedUntil) != null
            ? new Date(parseBlockedUntilMs(blockedUntil) as number).toISOString()
            : null);
    const until = formatCooldownUntil(blockedUntilIso);
    return {
        title: 'Temporary cancellation cooldown',
        content: until
            ? `After a cancellation, new temp-shift booking is paused until ${until}. You can still browse shifts and return when the cooldown ends.`
            : 'After a cancellation, new temp-shift booking is temporarily paused. You can still browse shifts and return when the cooldown ends.',
        ctaLabel: 'Back to calendar',
        code: 'CANCELLATION_COOLDOWN',
    };
};

const getOverlayBannerCopy = (
    membership: MembershipStatusResponse,
    context: MembershipBannerContext,
): MembershipBannerCopy | null => {
    const overlay = membership.overlay;
    if (!overlay?.active) return null;
    const overlayEnforced = isRecoveryOverlayEnforced(membership);

    switch (overlay.state) {
        case 'course_required':
            return {
                tone: overlayEnforced ? 'warning' : 'info',
                title: 'Please complete your required course',
                body: overlayEnforced
                    ? 'Your temp-shift access is paused until the assigned course is completed.'
                    : 'Under the upcoming membership reform, your temp-shift access would be paused until the assigned course is completed.',
            };
        case 'restricted_reentry':
            return {
                tone: overlayEnforced ? 'warning' : 'info',
                title: 'Restricted re-entry',
                body: overlayEnforced
                    ? 'Welcome back. Shift access is temporarily limited to close, near-term shifts while you rebuild your standing.'
                    : 'Preview of your upcoming post-course restricted re-entry limits.',
            };
        case 'on_a_break': {
            const until = formatBreakUntil(overlay.onABreakUntil);
            return {
                tone: overlayEnforced ? 'warning' : 'info',
                title: 'Your account is temporarily on hold',
                body: until
                    ? `We care about setting dental professionals up for success, especially early in their careers. Your temping access is on hold until ${until}; after that date, we will re-enable your account so you can view and book temping shifts.`
                    : 'We care about setting dental professionals up for success, especially early in their careers. Your temping access is temporarily on hold; after the hold, we will re-enable your account so you can view and book temping shifts.',
            };
        }
        case 'not_a_fit': {
            switch (overlay.reasonCategory) {
                case 'credential_review': {
                    const firstName = context.firstName?.trim();
                    const greeting = firstName ? `Hi ${firstName}!` : 'Hi!';
                    const supportLabel = 'email the TempStars Support Team';
                    return {
                        tone: 'warning',
                        title: 'Your account details are under review',
                        body: `${greeting} Our Membership Management Team is reviewing your account details. Just sit tight—this typically takes less than 2 business days, and we'll get back to you when the review is complete. If you need anything in the meantime or have any questions, you can ${supportLabel}.`,
                        inlineAction: {
                            label: supportLabel,
                            path: 'hygienistContactSupport',
                        },
                    };
                }
                case 'administrative_block':
                    return {
                        tone: 'error',
                        title: 'Temping access is currently unavailable',
                        body: 'Your account has been restricted by our Membership Management Team. Contact support if you need help understanding this decision.',
                    };
                case 'legacy_import':
                    return {
                        tone: 'error',
                        title: 'Temping access is unavailable',
                        body: 'Your account is not currently eligible for temping or new Job Board access. Contact support if you have questions about your account.',
                    };
                case 'system_outcome':
                    return {
                        tone: 'error',
                        title: 'Temping access is no longer available',
                        body: 'We care about setting everyone up for success. Based on your recent TempStars history, temping and new Job Board access are no longer a fit right now.',
                    };
                default:
                    return {
                        tone: 'error',
                        title: 'Temping access is currently unavailable',
                        body: 'Your temping and new Job Board access are currently unavailable. Contact support if you have questions about your account.',
                    };
            }
        }
        case 'duplicate_review': {
            const matchHint = overlay.maskedEmail ? ` (${overlay.maskedEmail})` : '';
            return {
                tone: 'warning',
                title: "Let's get your account sorted",
                body: `Your details closely match an existing TempStars account${matchHint}, so shift access is paused while we sort this out. `
                    + 'Already have an account? Use Forgot Password with your original email address — you\'ll be back in minutes. '
                    + 'Not yours? Contact support and we\'ll fix it quickly.',
            };
        }
        default:
            return null;
    }
};

export const getMembershipBannerCopy = (
    membership: MembershipStatusResponse,
    context: MembershipBannerContext = {},
): MembershipBannerCopy => {
    const overlayCopy = getOverlayBannerCopy(membership, context);
    if (overlayCopy) return overlayCopy;

    switch (membership.tempShiftAccess) {
        case 'protected':
            return {
                tone: 'info',
                title: 'You are protected during rollout',
                body: 'Grandfathering is currently preserving your temp-shift access while Fair Fresh Start rolls out. Keep in mind: protection does not grant immunity — continued poor feedback or shift cancellations can still cause your status to drop.',
            };
        case 'restricted':
            return {
                tone: membership.mode === 'enforced' ? 'warning' : 'info',
                title: membership.mode === 'enforced' ? 'Some shift access is currently limited' : 'Preview of upcoming shift limits',
                body: membership.mode === 'enforced'
                    ? 'Some shifts may be hidden or blocked based on your current Fair Fresh Start restrictions.'
                    : 'You are seeing the Fair Fresh Start preview. The limits below are not fully live yet.',
            };
        default:
            return {
                tone: 'info',
                title: 'Membership status',
                body: 'View My Stats to see your current Fair Fresh Start details.',
            };
    }
};

export const getMembershipRestrictionLines = (
    restrictions?: MembershipStatusResponse['restrictions'] | null,
    options: { hideBookedShiftLimit?: boolean } = {},
): string[] => {
    if (!restrictions) return [];

    const lines: string[] = [];
    if (restrictions.maxDistanceKm != null) {
        lines.push(`Travel distance: up to ${restrictions.maxDistanceKm} km`);
    }
    if (restrictions.maxLeadTimeDays != null) {
        lines.push(`Advance booking window: ${restrictions.maxLeadTimeDays} day${restrictions.maxLeadTimeDays === 1 ? '' : 's'} or less`);
    }
    if (!options.hideBookedShiftLimit && restrictions.effectiveMaxBookedShifts != null) {
        lines.push(`Confirmed shifts at once: up to ${restrictions.effectiveMaxBookedShifts}`);
    }
    return lines;
};

export const getMembershipRestrictionSummaryLines = (
    restrictions?: MembershipStatusResponse['restrictions'] | null,
): string[] => {
    if (!restrictions) return [];

    const lines: string[] = [];
    const hasReliabilityLimits = restrictions.maxDistanceKm != null || restrictions.maxLeadTimeDays != null;
    const hasFeedbackLimit = restrictions.effectiveMaxBookedShifts != null;

    if (hasReliabilityLimits) {
        if (restrictions.maxDistanceKm != null && restrictions.maxLeadTimeDays != null) {
            lines.push('We are focusing your shift visibility on closer, near-term opportunities while you rebuild reliability.');
        } else if (restrictions.maxDistanceKm != null) {
            lines.push('We are focusing your shift visibility on opportunities closer to home while you rebuild reliability.');
        } else {
            lines.push('We are focusing your shift visibility on near-term opportunities while you rebuild reliability.');
        }
    }

    if (hasFeedbackLimit) {
        lines.push('Your booking capacity is temporarily limited while you rebuild feedback standing.');
    }

    return lines;
};

const normalizeCopy = (value?: string | null): string => (
    value || ''
).trim().toLowerCase().replace(/\s+/g, ' ');

export const shouldRenderMembershipNextAction = (
    membership?: MembershipStatusResponse | null,
    banner?: MembershipBannerCopy | null,
    options: { hideReviewRestrictions?: boolean } = {},
): boolean => {
    if (!membership?.nextAction) return false;

    // Overlay banners already carry the concrete recovery message. Rendering
    // the API nextAction under them repeats CourseRequired/hold/Not-a-Fit copy.
    if (membership.overlay?.active) return false;

    if (options.hideReviewRestrictions && membership.nextAction.type === 'review_restrictions') {
        return false;
    }

    if (!banner) return true;
    const title = normalizeCopy(membership.nextAction.title);
    const description = normalizeCopy(membership.nextAction.description);
    return title !== normalizeCopy(banner.title) && description !== normalizeCopy(banner.body);
};

export type MembershipRestrictionDialogCopy = {
    title: string;
    content: string;
    ctaLabel: string;
    ctaPath?: string;
    code: string;
};

export const getMembershipRestrictionDialogCopy = (error: unknown): MembershipRestrictionDialogCopy | null => {
    const membershipError = getMembershipRestrictionError(error);
    if (!membershipError) return null;

    let title = 'Shift unavailable right now';
    let content = 'This shift is outside your current Fair Fresh Start access.';
    let defaultCtaLabel = 'View My Stats';
    let defaultCtaPath = 'myStats';

    switch (membershipError.code) {
        case 'DISTANCE_EXCEEDED':
            title = 'Outside your current travel limit';
            content = 'This shift is farther away than your current Fair Fresh Start travel limit allows.';
            break;
        case 'LEAD_TIME_EXCEEDED':
            title = 'Outside your current booking window';
            content = 'This shift is too far in advance for your current Fair Fresh Start booking window.';
            break;
        case 'MAX_BOOKED_REACHED':
            title = 'You are at your current shift limit';
            content = 'You have reached the maximum number of confirmed shifts allowed by your current Fair Fresh Start access.';
            break;
        case 'COURSE_REQUIRED':
            title = 'Please complete your required course';
            content = 'Your access is paused until the assigned course is completed. My Stats explains your current status and next step.';
            break;
        case 'ON_A_BREAK':
            title = 'Your account is temporarily on hold';
            content = 'Your account is on a temporary hold. My Stats explains your hold date and how restricted re-entry works when the hold is lifted.';
            break;
        case 'NOT_A_FIT':
            title = 'Temping access is no longer available';
            content = 'Based on your recent TempStars history, temping and new Job Board access are no longer a fit right now. My Stats explains your current status.';
            break;
        case 'DUPLICATE_REVIEW':
            title = 'Account under review';
            content = 'Booking is paused while we sort out a possible duplicate account. Check your email for details — if the matching account is yours, '
                + 'use Forgot Password with your original email address. Otherwise contact support and we\'ll fix it quickly.';
            defaultCtaLabel = 'Contact Support';
            defaultCtaPath = 'hygienistContactSupport';
            break;
        case 'CANCELLATION_COOLDOWN': {
            const cooldownCopy = getCancellationCooldownDialogCopy(membershipError.details?.blockedUntil);
            return {
                title: cooldownCopy.title,
                content: cooldownCopy.content,
                ctaLabel: cooldownCopy.ctaLabel,
                code: membershipError.code || 'CANCELLATION_COOLDOWN',
            };
        }
        default:
            break;
    }

    return {
        title,
        content,
        ctaLabel: membershipError.ctaLabel || defaultCtaLabel,
        ctaPath: membershipError.ctaPath || defaultCtaPath,
        code: membershipError.code || 'UNKNOWN',
    };
};

const MEMBERSHIP_STATUS_RANKS: Record<string, number> = {
    Revoked: 0,
    Suspended: 0,
    CourseRequired: 0,
    Probation: 1,
    Provisional: 1,
    RL2: 1,
    FL2: 1,
    RL2FL1: 1,
    RL1FL2: 1,
    RL2FL2: 1,
    RL1: 2,
    FL1: 2,
    RL1FL1: 2,
    Active: 2,
    Pro: 3,
    Elite: 4,
};

export const getMembershipStatusRank = (statusCode?: string | null): number => {
    if (!statusCode) return -1;
    return MEMBERSHIP_STATUS_RANKS[statusCode] ?? -1;
};

export const isAdminTempingGoodStanding = (
    membership?: Pick<MembershipStatusResponse, 'currentStatusCode'> | null,
): boolean => getMembershipStatusRank(membership?.currentStatusCode) >= getMembershipStatusRank('Active');

export const getEffectiveRateTier = (
    membership?: MembershipDecisionInput | null,
): 0 | 1 | 2 => {
    if (!hasCompleteMembershipDecision(membership)) {
        return 0;
    }
    switch (membership?.currentStatusCode) {
        case 'Elite':
            return 2;
        case 'Pro':
            return 1;
        default:
            return 0;
    }
};

export const getRateCalculationTalent = (
    hygienist?: Pick<HygienistProps, 'graduationYear' | 'status'> | null,
    membership?: MembershipDecisionInput | null,
): Pick<HygienistProps, 'graduationYear' | 'status'> => ({
    graduationYear: hygienist?.graduationYear ?? 0,
    status: getEffectiveRateTier(membership),
});

export const canUseInstabookByMembership = ({
    membership,
    isFavourite,
}: {
    membership?: MembershipDecisionInput | null;
    isFavourite?: boolean;
}): boolean => {
    if (!hasCompleteMembershipDecision(membership)) return false;
    // An active membership-recovery overlay must gate Instabook, regardless
    // of whether the overlay is emitted by the ValueGives provisional lane
    // or the FFS Phase 2 established-lane recovery path. Users in
    // `course_required` / `not_a_fit` should never Instabook, and users in
    // `restricted_reentry` should follow the RL2FL2 booking cap rather than
    // being auto-elevated. Gating is only hard when the owning recovery engine
    // is live, with top-level enforced mode kept as a backward-compatible fallback.
    if (isBlockingRecoveryOverlay(membership)) {
        return false;
    }
    if (!isFfsGoodStandingForDeleteAccount(membership)) return false;
    return getEffectiveRateTier(membership) > 0 || !!isFavourite;
};

function hasCompleteMembershipDecision(
    membership?: MembershipDecisionInput | null,
): membership is MembershipDecisionInput & {
    currentStatusCode: string;
    tempShiftAccess: TempShiftAccess;
} {
    const statusCode = typeof membership?.currentStatusCode === 'string'
        ? membership.currentStatusCode.trim()
        : '';
    if (
        !statusCode
        || !MEMBERSHIP_STATUS_CODES.has(statusCode)
        || !membership?.tempShiftAccess
        || !TEMP_SHIFT_ACCESS_VALUES.has(membership.tempShiftAccess)
    ) return false;

    const overlayState = membership.membershipOverlayState ?? membership.overlay?.state ?? null;
    if (overlayState != null && !MEMBERSHIP_OVERLAY_STATES.has(overlayState)) return false;

    // Requester job-detail projections use the explicit authority flag and do
    // not carry rollout mode. Full self-membership responses must carry a
    // recognized mode so overlay-less CourseRequired can mirror the API gate.
    if (membership.membershipAuthorityAvailable === true) return true;
    return membership.mode === 'shadow' || membership.mode === 'enforced';
}

const hasGlobalAutobookHardStop = (
    membership?: MembershipDecisionInput | null,
): boolean => {
    const state = membership?.membershipOverlayState ?? membership?.overlay?.state ?? null;
    if (membership?.currentStatusCode === 'CourseRequired') {
        // The six-field requester fallback has no rollout mode, so it cannot
        // prove that CourseRequired is shadow-only and must fail closed.
        if (membership.membershipAuthorityAvailable === true) return true;
        if (membership.mode === 'enforced') return true;
    }
    if (state === 'restricted_reentry') return false;
    if (state === 'duplicate_review') {
        return membership?.membershipAuthorityAvailable === true || membership?.overlay?.active === true;
    }
    return isBlockingRecoveryOverlay(membership);
};

/**
 * Auto-book schedule management is not Instabook: it does not require a
 * Pro/Elite tier or favourite-office grant. Exact-job limitations remain
 * server-owned when an enabled schedule later produces a pending request.
 */
export const canManageAutobookScheduleByMembership = (
    membership?: MembershipDecisionInput | null,
): boolean => hasCompleteMembershipDecision(membership) && !hasGlobalAutobookHardStop(membership);

/**
 * Pending Auto-book confirmation uses canonical global hard stops only. The
 * confirmation API owns restricted membership's job-specific cap, distance,
 * and lead-time validation, just as the automatic confirmation path does.
 */
export const canConfirmAutobookByMembership = (
    membership?: MembershipDecisionInput | null,
): boolean => hasCompleteMembershipDecision(membership) && !hasGlobalAutobookHardStop(membership);

export type CanonicalMembershipBadge = 'pro' | 'elite' | null;

/**
 * Pro/Elite artwork may only be selected from a server-provided canonical
 * membership code/label. Unknown or absent values deliberately render no badge.
 */
export const getCanonicalMembershipBadge = (
    statusCodeOrLabel?: string | null,
): CanonicalMembershipBadge => {
    if (statusCodeOrLabel === 'Pro') return 'pro';
    if (statusCodeOrLabel === 'Elite') return 'elite';
    return null;
};

export const canRequestMaxBookedIncrease = (
    membership: MembershipDecisionInput | null | undefined,
    completedShifts: number,
): boolean => hasCompleteMembershipDecision(membership)
    && completedShifts >= 3
    && isFfsGoodStandingForDeleteAccount(membership);

export const canShowAutobookActivationCta = (
    membership: MembershipDecisionInput | null | undefined,
    completedShiftsLast30: number,
): boolean => {
    if (!hasCompleteMembershipDecision(membership) || !isFfsGoodStandingForDeleteAccount(membership)) return false;
    if (membership.currentStatusCode === 'Pro' || membership.currentStatusCode === 'Elite') return true;
    return completedShiftsLast30 >= 6;
};

export const shouldBlockAvailableNowByMembership = (
    membership?: MembershipStatusResponse | null,
): boolean => {
    if (!membership) return false;

    if (isBlockingRecoveryOverlay(membership)) return true;

    if (membership.mode !== 'enforced') return false;

    return membership.tempShiftAccess === 'restricted';
};

type AppHistory = { push: (path: string | { pathname: string }) => void };

type GoReactApi = { TempStars?: { App?: { goReact?: (path: string) => void } } };

/**
 * Centralised CTA-path router for membership banners / dialogs.
 *
 * The API may emit any of these paths (see `membership.service.ts`
 * `buildNextActionWithOverlay` + `getMembershipRestrictionDialogCopy`):
 *   myStats | availableJobs | coursesHome | hygienistContactSupport
 *
 * Legacy `contactSupport` is kept as an alias because it still appears in
 * older CTA handlers; new code should use `hygienistContactSupport`.
 */
export const openMembershipPath = (history: AppHistory, path: string | undefined): void => {
    const target = (path || 'myStats').trim();
    const goReact = (window as unknown as GoReactApi).TempStars?.App?.goReact;
    switch (target) {
        case 'myStats':
            if (goReact) {
                goReact('myStats');
            } else {
                history.push('myStats');
            }
            return;
        case 'availableJobs':
            history.push('availableJobs');
            return;
        case 'coursesHome':
            history.push('coursesHome');
            return;
        case 'hygienistContactSupport':
        case 'contactSupport':
            if (goReact) {
                goReact('hygienistContactSupport');
            } else {
                history.push('hygienistContactSupport');
            }
            return;
        default:
            if (goReact) {
                goReact('myStats');
            } else {
                history.push('myStats');
            }
    }
};
